/**
 * Follow-up registry — maps the LAST tool a turn used to a small set of
 * suggested next-action chips rendered below the assistant message.
 *
 * HANDOFF_AI_CHAT_FEATURE_V1_REVISED orchestration spec §4.
 *
 * Max 3 chips per assistant turn (orchestration spec §1). Frontend deduplicates
 * across multi-tool turns by using only the last tool's chips.
 */

export interface FollowUpChip {
  /** Stable id, used for analytics. */
  id: string;
  /** Visible label (sentence case, no trailing period). */
  label: string;
  /** Prompt text inserted into the input when the chip is clicked. */
  prompt: string;
}

export const followUpRegistry: Record<string, FollowUpChip[]> = {
  get_latest_term_sheet: [
    { id: 'walk-terms', label: 'Walk me through the key terms', prompt: 'Walk me through the key financial terms in this term sheet.' },
    { id: 'market-comparison', label: 'Compare this to the market', prompt: 'How does this term sheet compare to the market?' },
    { id: 'negotiate', label: 'What can I push back on?', prompt: 'Which terms in this term sheet have the most room to negotiate?' },
  ],
  list_deals: [
    { id: 'open-best-scoring', label: 'Open the highest-scoring deal', prompt: 'Show the deal with the highest score.' },
    { id: 'whats-pending', label: 'What is still in extraction?', prompt: 'Which deals still have pending extractions?' },
  ],
  get_deal: [
    { id: 'show-extraction', label: 'Show the extracted terms', prompt: 'Show me the extracted financial terms for this deal.' },
    { id: 'show-verdict', label: 'Show the verdict', prompt: "What's the verdict on this deal?" },
    { id: 'show-benchmarks', label: 'Show the market comparison', prompt: 'Compare this deal to the market.' },
  ],
  get_verdict: [
    { id: 'why-weak', label: 'What is dragging my score down?', prompt: 'Why did the weakest component score low?' },
    { id: 'show-benchmarks', label: 'Show the market comparison', prompt: 'Compare this deal to the market.' },
    { id: 'negotiate', label: 'What can I push back on?', prompt: 'Given my score, which terms have the most room to negotiate?' },
  ],
  get_analysis: [
    { id: 'show-benchmarks', label: 'Show the market comparison', prompt: 'Compare these components to the market.' },
    { id: 'fees-detail', label: 'Explain my fees in detail', prompt: 'Explain my fees in detail.' },
    { id: 'negotiate', label: 'What can I push back on?', prompt: 'Given my score, which terms have the most room to negotiate?' },
  ],
  get_active_extraction: [
    { id: 'show-benchmarks', label: 'Compare the rate to the market', prompt: 'How does this margin compare to the market?' },
    { id: 'covenant-check', label: 'Are any covenants unusual?', prompt: 'Are any of the covenants in this term sheet unusual for a deal like mine?' },
    { id: 'show-verdict', label: 'Show the verdict', prompt: "What's the verdict on this deal?" },
  ],
  get_extraction: [
    { id: 'show-benchmarks', label: 'Compare the rate to the market', prompt: 'How does this margin compare to the market?' },
    { id: 'covenant-check', label: 'Are any covenants unusual?', prompt: 'Are any of the covenants in this term sheet unusual for a deal like mine?' },
  ],
  get_repayment_shape: [
    { id: 'total-cost', label: 'What does it cost all-in?', prompt: 'What is the total cost of credit for this deal?' },
    { id: 'prepay', label: 'Can I repay early?', prompt: 'What are my prepayment options and penalties?' },
  ],
  get_cost_of_credit: [
    { id: 'repayment-shape', label: 'When do I pay?', prompt: 'What is my repayment schedule and monthly payment?' },
    { id: 'benchmark-rate', label: 'Is this cheap for the market?', prompt: 'How does my rate compare to the market?' },
  ],
  get_covenant_coverage: [
    { id: 'explain-leverage', label: 'What does my leverage covenant mean?', prompt: 'What does my leverage covenant mean and is it tight?' },
    { id: 'coverage-breach', label: 'What happens if I breach one?', prompt: 'What are the typical consequences if I breach one of these covenants?' },
    { id: 'negotiate', label: 'What can I push back on?', prompt: 'Given these covenants, which terms have the most room to negotiate?' },
  ],
  get_covenant_explanation: [
    { id: 'covenant-headroom', label: 'How much headroom do I have?', prompt: 'How much headroom do I have on this covenant before I breach it?' },
    { id: 'covenant-breach', label: 'What happens if I breach it?', prompt: 'What are the typical consequences if I breach this covenant?' },
    { id: 'covenant-standard', label: 'Is this threshold standard?', prompt: 'Is this covenant threshold standard for a deal like mine?' },
  ],
  get_personal_guarantee_risk: [
    { id: 'pg-negotiate', label: 'Can I negotiate the guarantee down?', prompt: 'What room is there to negotiate or cap this personal guarantee?' },
    { id: 'pg-what-if-breach', label: 'What happens if I breach?', prompt: 'What happens to my personal guarantee if I breach a covenant?' },
    { id: 'show-security', label: 'Show the full security package', prompt: 'Show me the full security and collateral package for this deal.' },
  ],
  get_benchmarks: [
    { id: 'show-component-detail', label: 'Break down each component', prompt: 'Break down each component vs the market.' },
    { id: 'all-in-rate', label: "What's my all-in rate?", prompt: 'What is my all-in rate combining the current EURIBOR with my deal margin?' },
    { id: 'negotiate', label: 'What can I push back on?', prompt: 'Given my benchmark results, which terms have the most room to negotiate?' },
  ],
  get_market_rates: [
    { id: 'all-in-cost', label: 'What does this mean for my total interest cost?', prompt: 'Given current EURIBOR and my deal margin, what is my total annual interest cost?' },
  ],
  compare_term_sheets: [
    { id: 'show-changes', label: 'What changed and why it matters', prompt: 'What changed between the versions and why it matters.' },
    { id: 'negotiate-changes', label: 'What should I accept vs push back on?', prompt: 'Based on what changed, what should I accept and what should I negotiate?' },
  ],
  update_deal_notes: [
    { id: 'negotiate-next', label: 'What should I negotiate next?', prompt: 'Given this deal analysis, which terms have the most room to negotiate?' },
  ],
  mark_deal_status: [
    { id: 'compare-portfolio', label: 'How does this compare to my other deals?', prompt: 'How does this deal compare to my other deals?' },
  ],
  archive_deal: [
    { id: 'show-deals', label: 'Show my other deals', prompt: 'Show me all my active deals.' },
  ],
  update_deal_field: [
    { id: 'run-analysis', label: 'Run analysis', prompt: 'Run the analysis for this deal.' },
  ],
  generate_export: [
    { id: 'share-with-accountant', label: 'Tips for sharing with my accountant', prompt: 'What should I highlight when I share this analysis with my accountant?' },
    { id: 'what-to-negotiate', label: 'What can I push back on?', prompt: 'Given this analysis, which terms have the most room to negotiate?' },
  ],
};
